import { useState } from 'react'
import { BrowserRouter, useLocation, useNavigate } from 'react-router'
import { Tabs, tabId, tabPanelId } from './ui'
import type { TabItem } from './ui'
import { AppDisclaimer } from './components/AppDisclaimer'
import { AppRoot } from './components/AppRoot'
import { AdminScreen } from './screens/AdminScreen'
import { CalendarScreen } from './screens/CalendarScreen'
import { FeedScreen } from './screens/FeedScreen'
import { JoinScreen } from './screens/JoinScreen'
import { SettingsScreen } from './screens/SettingsScreen'
import { SetupScreen } from './screens/SetupScreen'
import { SignInScreen } from './screens/SignInScreen'
import { SwapsScreen } from './screens/SwapsScreen'
import { AppRoutes } from './routes/AppRoutes'
import { SessionProvider } from './lib/session-context'
import { todayIso } from './lib/today'
import type { IsoDate } from './engine/types'

/** The app's four sections. Feed is the landing tab — PLAN.md §8. */
type Tab = 'feed' | 'calendar' | 'swaps' | 'settings'

const TAB_ITEMS: TabItem<Tab>[] = [
  { value: 'feed', label: 'Feed' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'swaps', label: 'Swaps' },
  { value: 'settings', label: 'Settings' },
]

/**
 * The URL each tab lives at. Feed keeps `/` — it is the landing tab, and
 * `AppRoutes`' shell is the thing rendered at `/`, so a signed-in READY user
 * opening the app lands on Feed with no redirect. The other three are real
 * sub-paths, which `public/_redirects`' SPA fallback keeps alive on refresh
 * and on a shared link.
 */
const TAB_PATHS: Record<Tab, string> = {
  feed: '/',
  calendar: '/calendar',
  swaps: '/swaps',
  settings: '/settings',
}

/**
 * The tab a URL selects, or `'feed'` for anything the shell doesn't own.
 *
 * The fallback matters: `AppRoutes`'s `/` route is a splat (`/*`) so it
 * matches every path the four more specific routes don't, and a READY user
 * who types or follows an unknown path sees the landing tab rather than a
 * blank page. `/calendar`, `/swaps` and `/settings` must match exactly — a
 * prefix match would be fine today but would silently break if a later phase
 * ever nested a real route under one of them (e.g. `/swaps/123`).
 */
function tabFromPathname(pathname: string): Tab {
  for (const tab of Object.keys(TAB_PATHS) as Tab[]) {
    if (pathname === TAB_PATHS[tab]) return tab
  }
  return 'feed'
}

// Shared by the tab strip and the panel it labels — see Tabs.tsx's idBase
// convention (tabId / tabPanelId).
const TABS_ID_BASE = 'app'

/**
 * The four-tab shell — Phase 1's structure, with Phase 1's one remaining seam
 * swapped out: the tab is no longer local `useState`, it is the URL.
 *
 * The strip renders exactly as before (`Tabs` is untouched, vendored-code
 * rules apply), fed by two router hooks instead of a setter:
 *
 * - URL → tab: `useLocation().pathname` through `tabFromPathname`, so the
 *   active tab survives refresh, deep links and the back button.
 * - tab → URL: `useNavigate`, so clicking (or arrowing through) the strip
 *   pushes a history entry and `AppRoutes` re-renders the same shell with the
 *   new panel. No screen or `Tabs` prop knows a router exists.
 *
 * `AppRoutes` renders this component for every path its `/` splat route
 * matches while `decideGate` says `READY`, so the panel below the strip is
 * the only thing that changes on navigation — the gate logic never re-runs
 * per tab.
 *
 * Since Phase 3 the shell no longer reads the profile at all: every screen
 * that needs it calls `useSession()` itself (the same seam
 * `SetupScreen`/`AdminScreen`/`SettingsScreen` already use). `CalendarScreen`
 * in particular reads the profile directly — block, roster type and week
 * start all live behind its own `useSession()` call, because it needs the
 * user id to fetch roster data anyway — so the shell forwards it only
 * `today`, injected so the screen never reads the clock. The shell is only
 * ever rendered once the gate says `READY`, so the profile is non-null and
 * complete in practice; the screen keeps a defensive empty state consistent
 * with `isProfileComplete` for the case where that precondition loosens.
 */
export function AppShell() {
  const location = useLocation()
  const navigate = useNavigate()
  // Read once per mount rather than on every render — "today" should not
  // shift under the user's feet while the tab stays open, and `useState`'s
  // initialiser form is what keeps this the single read.
  const [today] = useState<IsoDate>(() => todayIso())

  const tab = tabFromPathname(location.pathname)

  return (
    <AppRoot>
      <Tabs
        label="Sections"
        items={TAB_ITEMS}
        value={tab}
        onChange={(next) => navigate(TAB_PATHS[next])}
        idBase={TABS_ID_BASE}
      />

      {/* Exactly one panel renders — the active screen — never all four. */}
      <div
        role="tabpanel"
        id={tabPanelId(TABS_ID_BASE, tab)}
        aria-labelledby={tabId(TABS_ID_BASE, tab)}
        // The panel itself is a tab stop: arrowing to a tab and pressing
        // Tab should land in the body that tab just revealed, rather than
        // skipping past it — Tabs.tsx puts tabIndex={-1} on unselected
        // tabs and EmptyState contains nothing else focusable.
        tabIndex={0}
      >
        {tab === 'feed' && <FeedScreen today={today} />}
        {tab === 'calendar' && <CalendarScreen today={today} />}
        {tab === 'swaps' && <SwapsScreen />}
        {tab === 'settings' && <SettingsScreen />}
      </div>

      <AppDisclaimer />
    </AppRoot>
  )
}

/**
 * The five routes with the five real screens wired in.
 *
 * Lives one level below the providers rather than directly in `App` for one
 * reason: `JoinScreen`'s `onRedeemed` (Task 14) is "navigate home, and let
 * `AppRoutes`'s gate send the now-ACTIVE user to SETUP or READY" — and a
 * `navigate` callback can only be created inside the router context, which
 * `App` itself sits above. It is the only piece of navigation this file owns.
 */
function RouterHost() {
  const navigate = useNavigate()

  return (
    <AppRoutes
      shell={<AppShell />}
      setup={<SetupScreen />}
      signIn={<SignInScreen />}
      join={<JoinScreen onRedeemed={() => navigate('/')} />}
      admin={<AdminScreen />}
    />
  )
}

/**
 * The app's entry point, and from Phase 2 on, nothing more than the host of
 * three wrappers:
 *
 * - `<BrowserRouter>` — react-router v7 in library mode (imported from
 *   `react-router`, not `react-router-dom`; v7 renamed the package). Owns the
 *   URL; Cloudflare Pages' `public/_redirects` SPA fallback is what keeps
 *   deep URLs like `/join?code=…` alive on refresh.
 * - `<SessionProvider>` — the one `supabase.auth` subscription in the app
 *   (Task 10). Everything downstream reads auth state through `useSession()`.
 * - `<AppRoutes>` (via `RouterHost`, for the `navigate` seam above) — the
 *   five routes and the `decideGate` gate (Task 12). The five real screens
 *   are passed in as the route content: `AppShell` for `READY`, `SetupScreen`
 *   for `SETUP`, and the sign-in / join / admin screens for their paths.
 *
 * Phase 1's synchronous onboarding gate (`readLocalProfile()` in a
 * `useState` initialiser, `Onboarding` vs `AppShell`) is gone entirely: the
 * profile is async now, and that decision moved to `decideGate`, run inside
 * `AppRoutes` off the session context. `Onboarding` is superseded by
 * `SetupScreen`, which absorbs its block-picker → four-week-confirm flow
 * against the real `profiles` row. `AppRoot`'s per-screen `StationLedger`
 * wrapper is deliberately NOT hoisted up here — every top-level screen
 * already wraps itself in exactly one, and a second one here would double
 * the chrome (the bug Task 14 fixed once already in `JoinScreen`).
 */
export function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <RouterHost />
      </SessionProvider>
    </BrowserRouter>
  )
}
